import {
  ApiBlueprint,
  DiscoveryApi,
  FetchApi,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';

// The types mirror plugins/azure-costs-backend (CostReports, CostStore).

export type Preset =
  | 'this-month'
  | 'last-month'
  | 'last-30d'
  | 'last-90d'
  | 'last-12m';

/** What a request asks for: a preset, or a `from`…`to` range. */
export type PeriodSelection = { preset: Preset } | { from: string; to: string };

export type Range = { from: string; to: string };

export type PeriodInfo = Range & {
  preset?: Preset;
  previous: Range;
  granularity: 'day' | 'month';
  /** Set when the period is the current month: projection and forecast apply. */
  currentMonth?: string;
};

type Header = { period: PeriodInfo; currency?: string; dataAsOf?: string };

export type SystemSummary = {
  system: string;
  cost: number;
  previous: number;
  projection?: number;
  sharedCost: number;
  bySubscription: Record<string, number>;
};

export type SubscriptionSummary = {
  subscriptionId: string;
  name: string;
  cost: number;
  previous: number;
  forecast?: number;
};

export type Summary = Header & {
  systems: SystemSummary[];
  subscriptions: SubscriptionSummary[];
  total: {
    cost: number;
    previous: number;
    forecast?: number;
    projection?: number;
  };
};

export type Dimension =
  | 'system'
  | 'component'
  | 'subscription'
  | 'service'
  | 'resourceGroup'
  | 'resource';

export type BreakdownItem = {
  system?: string;
  component?: string;
  subscriptionId?: string;
  service?: string;
  resourceGroup?: string;
  resourceId?: string;
  resourceName?: string;
  resourceType?: string;
  entityRef?: string;
  cost: number;
  previous: number;
  projection?: number;
  sharedCost: number;
};

export type Breakdown = Header & { by: Dimension; items: BreakdownItem[] };

export type Mover = BreakdownItem & { change: number };

export type Movers = Header & { up: Mover[]; down: Mover[] };

export type Series = Header & {
  days: Array<{ date: string; key: string; cost: number }>;
};

export type MeterRow = {
  meter: string;
  meterSubCategory: string;
  cost: number;
  currency: string;
};

export type ResourceReport = Header & {
  resourceId: string;
  name: string;
  type: string;
  subscriptionId?: string;
  resourceGroup?: string;
  system?: string;
  component?: string;
  entityRef?: string;
  tags: { systems?: string[]; component?: string };
  lastSeenAt?: string;
  cost: number;
  previous: number;
  byService: Array<{ service: string; cost: number }>;
  days: Array<{ date: string; cost: number }>;
  meters?: MeterRow[];
};

export type EntityCosts = Header & {
  entityRef: string;
  cost: number;
  previous: number;
  projection?: number;
  days: Array<{ date: string; cost: number }>;
  topComponents?: BreakdownItem[];
};

export type SyncStatus = {
  subscriptions: Array<{
    latest: {
      subscriptionId: string;
      kind: 'backfill' | 'refresh';
      startedAt: string;
      finishedAt?: string;
      status: 'running' | 'succeeded' | 'failed';
      rows?: number;
      error?: string;
    };
    lastSucceededAt?: string;
  }>;
};

/** `component: ''` is "the costs of the system that have no component". */
export type Filter = {
  system?: string;
  component?: string;
  subscription?: string;
  /** Resources shared with this system but counted in another. */
  sharedWith?: string;
};

/** What the signed-in person may see (plan 005). */
export type MyAccess = {
  admin: boolean;
  owner: boolean;
  /** Granted systems; `['*']` for all. */
  systems: string[];
  /** Who to ask for access. */
  admins: string[];
};

export type AccessOverview = {
  grants: Array<{ userRef: string; systems: string[] }>;
  admins: Array<{ userRef: string; addedBy: string; addedAt: string }>;
  owners: Array<{
    principalId: string;
    userRef?: string;
    subscriptions: string[];
  }>;
  log: Array<{
    at: string;
    actor: string;
    action: 'grant' | 'revoke' | 'add-admin' | 'remove-admin';
    userRef: string;
    system?: string;
  }>;
  /** The systems a grant may name. */
  systems: string[];
};

export interface AzureCostsApi {
  summary(period: PeriodSelection): Promise<Summary>;
  daily(
    period: PeriodSelection,
    groupBy?: Dimension,
    filter?: Filter,
  ): Promise<Series>;
  breakdown(
    by: Dimension,
    period: PeriodSelection,
    filter?: Filter,
    limit?: number,
  ): Promise<Breakdown>;
  movers(period: PeriodSelection, filter?: Filter): Promise<Movers>;
  resource(
    id: string,
    period: PeriodSelection,
    meters?: boolean,
  ): Promise<ResourceReport>;
  /** A System's, Component's or Azure resource's costs; 403 for non-admins. */
  entity(entityRef: string, period: PeriodSelection): Promise<EntityCosts>;
  status(): Promise<SyncStatus>;
  /** Starts a sync now; the scheduler refuses if one is running. */
  sync(): Promise<void>;
  accessMe(): Promise<MyAccess>;
  /** Cost admins only. */
  accessOverview(): Promise<AccessOverview>;
  setGrants(userRef: string, systems: string[]): Promise<AccessOverview>;
  addAdmin(userRef: string): Promise<AccessOverview>;
  removeAdmin(userRef: string): Promise<AccessOverview>;
}

export const azureCostsApiRef = createApiRef<AzureCostsApi>().with({
  id: 'plugin.azure-costs.api',
  pluginId: 'azure-costs',
});

/** The query parameters of a period. */
export function periodQuery(period: PeriodSelection): Record<string, string> {
  return 'preset' in period
    ? { period: period.preset }
    : { from: period.from, to: period.to };
}

function filterQuery(filter: Filter = {}): Record<string, string | undefined> {
  return {
    system: filter.system,
    // The backend reads "-" as "no component".
    component:
      filter.component === undefined ? undefined : filter.component || '-',
    subscription: filter.subscription,
    sharedWith: filter.sharedWith,
  };
}

export class AzureCostsClient implements AzureCostsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  summary(period: PeriodSelection): Promise<Summary> {
    return this.get('/summary', periodQuery(period));
  }

  daily(
    period: PeriodSelection,
    groupBy?: Dimension,
    filter?: Filter,
  ): Promise<Series> {
    return this.get('/daily', {
      ...periodQuery(period),
      groupBy,
      ...filterQuery(filter),
    });
  }

  breakdown(
    by: Dimension,
    period: PeriodSelection,
    filter?: Filter,
    limit?: number,
  ): Promise<Breakdown> {
    return this.get('/breakdown', {
      by,
      ...periodQuery(period),
      ...filterQuery(filter),
      limit: limit === undefined ? undefined : String(limit),
    });
  }

  movers(period: PeriodSelection, filter?: Filter): Promise<Movers> {
    return this.get('/movers', {
      ...periodQuery(period),
      ...filterQuery(filter),
    });
  }

  resource(
    id: string,
    period: PeriodSelection,
    meters = false,
  ): Promise<ResourceReport> {
    return this.get('/resource', {
      id,
      ...periodQuery(period),
      meters: meters ? '1' : undefined,
    });
  }

  entity(entityRef: string, period: PeriodSelection): Promise<EntityCosts> {
    return this.get('/entity', { entityRef, ...periodQuery(period) });
  }

  status(): Promise<SyncStatus> {
    return this.get('/status', {});
  }

  async sync(): Promise<void> {
    await this.send('POST', '/sync');
  }

  accessMe(): Promise<MyAccess> {
    return this.get('/access/me', {});
  }

  accessOverview(): Promise<AccessOverview> {
    return this.get('/access', {});
  }

  setGrants(userRef: string, systems: string[]): Promise<AccessOverview> {
    return this.send('PUT', '/access/grants', { userRef, systems });
  }

  addAdmin(userRef: string): Promise<AccessOverview> {
    return this.send('POST', '/access/admins', { userRef });
  }

  removeAdmin(userRef: string): Promise<AccessOverview> {
    return this.send(
      'DELETE',
      `/access/admins?${new URLSearchParams({ userRef })}`,
    );
  }

  private async send<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('azure-costs');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method,
      ...(body !== undefined && {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  private async get<T>(
    path: string,
    query: Record<string, string | undefined>,
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('azure-costs');
    const params = new URLSearchParams(
      Object.entries(query).filter(
        (entry): entry is [string, string] => entry[1] !== undefined,
      ),
    ).toString();
    const response = await this.fetchApi.fetch(
      `${baseUrl}${path}${params ? `?${params}` : ''}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }
}

export const azureCostsApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: azureCostsApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new AzureCostsClient(discoveryApi, fetchApi),
    }),
});
